import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// Pages
import LoginPage from "./pages/LoginPage";
import SetupPage from "./pages/SetupPage";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import UserDashboard from "./pages/UserDashboard";
import PublicLeaderboard from "./pages/PublicLeaderboard";

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-void-black flex items-center justify-center">
                <span className="spinner" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user.role)) {
        // Redirect to appropriate dashboard based on role
        if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
        if (user.role === 'admin') return <Navigate to="/admin" replace />;
        if (user.role === 'user') {
            if (user.requires_setup) return <Navigate to="/setup" replace />;
            return <Navigate to="/user" replace />;
        }
        return <Navigate to="/" replace />;
    }

    return children;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-void-black flex items-center justify-center">
                <span className="spinner" />
            </div>
        );
    }

    if (user) {
        if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
        if (user.role === 'admin') return <Navigate to="/admin" replace />;
        if (user.role === 'user') {
            if (user.requires_setup) return <Navigate to="/setup" replace />;
            return <Navigate to="/user" replace />;
        }
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Leaderboard - accessible without login, supports slug or groupId */}
            <Route path="/klasemen/:groupSlug" element={<PublicLeaderboard />} />
            
            {/* Public routes */}
            <Route 
                path="/" 
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                } 
            />
            
            {/* Setup route - only for users requiring setup */}
            <Route 
                path="/setup" 
                element={
                    <ProtectedRoute allowedRoles={['user']}>
                        <SetupPage />
                    </ProtectedRoute>
                } 
            />

            {/* Super Admin routes */}
            <Route 
                path="/super-admin" 
                element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                        <SuperAdminDashboard />
                    </ProtectedRoute>
                } 
            />

            {/* Admin routes */}
            <Route 
                path="/admin" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminDashboard />
                    </ProtectedRoute>
                } 
            />

            {/* User routes */}
            <Route 
                path="/user" 
                element={
                    <ProtectedRoute allowedRoles={['user']}>
                        <UserDashboard />
                    </ProtectedRoute>
                } 
            />

            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <ThemeProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <AppRoutes />
                        <PWAInstallPrompt />
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </div>
    );
}

export default App;
